import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Pagination, Paper, Select, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Download, Refresh, Replay, PriceCheck, PlaylistAdd, Block } from '@mui/icons-material';
import billingService, { INVOICE_TYPE_LABELS, INVOICE_STATUS_LABELS, formatHuf } from '../../services/billingService';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import { useConfirm } from '../Common/ConfirmDialog';

const PAGE_SIZE = 20;

const formatDateTime = (value) => (value ? new Date(value).toLocaleString('hu-HU') : '–');

/**
 * Admin: a Wedigo által a szolgáltatóknak kiállított számlák (Billingo) — hibás / beragadt
 * számlák újrapróbálása, PDF letöltés.
 */
const AdminInvoicesPage = () => {
  const [confirm, confirmDialog] = useConfirm();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [forceTarget, setForceTarget] = useState(null);
  // Sztornó dialógus: { invoice, reason, refund, waiveCommission }
  const [cancelState, setCancelState] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingService.adminGetInvoices({ page, limit: PAGE_SIZE, status: statusFilter, type: typeFilter });
      setInvoices(res.data || []);
      setTotal(res.pagination?.total || 0);
      setBillingStatus(res.billing || null);
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'A számlák betöltése sikertelen.' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const retry = async (invoice, force = false) => {
    setBusyId(invoice.id);
    setMessage(null);
    try {
      const res = await billingService.adminRetryInvoice(invoice.id, force);
      setMessage({ severity: res.success ? 'success' : 'warning', text: res.message });
      await load();
    } catch (err) {
      if (err.data?.needsForce) {
        setForceTarget(invoice);
      } else {
        setMessage({ severity: 'error', text: err.message || 'Az újrapróbálás sikertelen.' });
      }
    } finally {
      setBusyId(null);
    }
  };

  const markPaid = async (invoice) => {
    if (!(await confirm({ title: 'Befizetés rögzítése', message: `Rögzíted, hogy a(z) ${invoice.invoiceNumber} számla (${formatHuf(invoice.grossAmount)}) átutalással befizetésre került? A befizetés a Billingóban is rögzül.`, confirmLabel: 'Befizetve' }))) return;
    setBusyId(invoice.id);
    setMessage(null);
    try {
      const res = await billingService.adminMarkInvoicePaid(invoice.id);
      setMessage({ severity: res.data?.lastError ? 'warning' : 'success', text: res.message });
      await load();
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'A befizetés rögzítése sikertelen.' });
    } finally {
      setBusyId(null);
    }
  };

  const runMonthly = async () => {
    if (!(await confirm({ title: 'Havi jutalékszámlák', message: 'Elkészíted az előző hónap jutalék-gyűjtőszámláit? (Szolgáltatónként és hónaponként csak egyszer készül számla.)', confirmLabel: 'Számlák elkészítése' }))) return;
    setLoading(true);
    setMessage(null);
    try {
      const res = await billingService.adminRunMonthlyCommission();
      setMessage({ severity: 'success', text: res.message });
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'A havi számlázás futtatása sikertelen.' });
    }
    await load();
  };

  const openCancel = (invoice) => setCancelState({
    invoice,
    reason: '',
    refund: false,
    waiveCommission: false,
  });

  const submitCancel = async () => {
    const { invoice, reason, refund, waiveCommission } = cancelState;
    setCancelState(null);
    setBusyId(invoice.id);
    setMessage(null);
    try {
      const res = await billingService.adminCancelInvoice(invoice.id, { reason, refund, waiveCommission });
      setMessage({ severity: 'success', text: res.message });
      await load();
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || 'A sztornó sikertelen.' });
    } finally {
      setBusyId(null);
    }
  };

  const download = async (invoice) => {
    setBusyId(invoice.id);
    try {
      await billingService.adminDownloadInvoicePdf(invoice);
    } catch (err) {
      setMessage({ severity: 'error', text: err.message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      {billingStatus && !billingStatus.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          A számlázás ezen a környezeten ki van kapcsolva (BILLING_DRIVER / BILLING_ENABLED) — az új számlák függőben maradnak.
        </Alert>
      )}
      {billingStatus?.driver === 'mock' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Mock számlázás: a számlák nem kerülnek a Billingóba, a számlaszámok „MOCK-” előtagúak.
        </Alert>
      )}
      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Állapot</InputLabel>
          <Select label="Állapot" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">Mind</MenuItem>
            {Object.entries(INVOICE_STATUS_LABELS).map(([key, { label }]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Típus</InputLabel>
          <Select label="Típus" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">Mind</MenuItem>
            {Object.entries(INVOICE_TYPE_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button startIcon={<Refresh />} onClick={load} disabled={loading}>Frissítés</Button>
        <Button startIcon={<PlaylistAdd />} onClick={runMonthly} disabled={loading} sx={{ ml: 'auto' }}>
          Havi jutalékszámlák (előző hónap)
        </Button>
      </Box>

      <ResponsiveTableContainer stickyLastColumn>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Szolgáltató</TableCell>
              <TableCell>Típus</TableCell>
              <TableCell>Számlaszám</TableCell>
              <TableCell align="right">Nettó</TableCell>
              <TableCell align="right">Bruttó</TableCell>
              <TableCell>Fizetve</TableCell>
              <TableCell>Állapot</TableCell>
              <TableCell>Létrehozva</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center">Nincs számla.</TableCell></TableRow>
            ) : invoices.map(invoice => {
              const status = INVOICE_STATUS_LABELS[invoice.status] || { label: invoice.status, color: 'default' };
              const canRetry = ['PENDING', 'FAILED', 'ISSUING'].includes(invoice.status);
              return (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.id}</TableCell>
                  <TableCell>{invoice.provider?.businessName || invoice.providerId}</TableCell>
                  <TableCell>{INVOICE_TYPE_LABELS[invoice.type] || invoice.type}</TableCell>
                  <TableCell>
                    {invoice.invoiceNumber || '–'}
                    {invoice.stornoInvoiceNumber && (
                      <Typography variant="caption" color="text.secondary" display="block">sztornó: {invoice.stornoInvoiceNumber}</Typography>
                    )}
                    {invoice.refundedAt && (
                      <Typography variant="caption" color="warning.main" display="block">visszatérítve: {formatHuf(invoice.refundAmount)}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatHuf(invoice.netAmount)}</TableCell>
                  <TableCell align="right">{formatHuf(invoice.grossAmount)}</TableCell>
                  <TableCell>
                    {invoice.paid
                      ? `Igen${invoice.paidAt ? ` (${new Date(invoice.paidAt).toLocaleDateString('hu-HU')})` : ''}`
                      : `Nem — határidő: ${new Date(invoice.dueDate).toLocaleDateString('hu-HU')}`}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={invoice.lastError || ''}>
                      <Chip size="small" label={`${status.label}${invoice.attempts > 1 ? ` (${invoice.attempts}×)` : ''}`} color={status.color} />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatDateTime(invoice.createdAt)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {busyId === invoice.id ? <CircularProgress size={18} /> : (
                      <>
                        {canRetry && (
                          <Tooltip title="Kiállítás újrapróbálása">
                            <IconButton size="small" onClick={() => retry(invoice)}><Replay fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {invoice.status === 'ISSUED' && !invoice.paid && (
                          <Tooltip title="Befizetés rögzítése (átutalás beérkezett)">
                            <IconButton size="small" onClick={() => markPaid(invoice)}><PriceCheck fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {invoice.status === 'ISSUED' && (
                          <Tooltip title="PDF letöltése">
                            <IconButton size="small" onClick={() => download(invoice)}><Download fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {['ISSUED', 'PENDING', 'FAILED'].includes(invoice.status) && (
                          <Tooltip title="Sztornó / visszatérítés">
                            <IconButton size="small" color="error" onClick={() => openCancel(invoice)}><Block fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={Math.ceil(total / PAGE_SIZE)} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}

      <Dialog open={!!cancelState} onClose={() => setCancelState(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Számla sztornózása</DialogTitle>
        {cancelState && (
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {cancelState.invoice.invoiceNumber || `#${cancelState.invoice.id}`} — {cancelState.invoice.provider?.businessName}, {formatHuf(cancelState.invoice.grossAmount)}.{' '}
              {cancelState.invoice.status === 'ISSUED'
                ? 'A Billingo sztornó bizonylatot állít ki, és elküldi a szolgáltatónak.'
                : 'A számla még nincs kiállítva, így csak a rendszerből törlődik (Billingo-bizonylat nem készül).'}
            </DialogContentText>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Sztornó oka (a bizonylatra is rákerül)"
              value={cancelState.reason}
              onChange={(e) => setCancelState(s => ({ ...s, reason: e.target.value }))}
              sx={{ mb: 1 }}
            />
            {cancelState.invoice.paid && cancelState.invoice.paymentId && (
              <FormControlLabel
                control={<Checkbox checked={cancelState.refund} onChange={(e) => setCancelState(s => ({ ...s, refund: e.target.checked }))} />}
                label={{
                  FEATURED_SUBSCRIPTION: 'Visszatérítés Barionon a szolgáltatónak — az előfizetés azonnal megszűnik',
                  BOOKING_COMMISSION: 'A jutalék visszatérítése Barionon a kliensnek (a szolgáltató részét a szolgáltató téríti)',
                  MONTHLY_COMMISSION: 'Visszatérítés Barionon a szolgáltatónak',
                }[cancelState.invoice.type]}
              />
            )}
            {cancelState.invoice.type !== 'FEATURED_SUBSCRIPTION' && (
              <FormControlLabel
                control={<Checkbox checked={cancelState.waiveCommission} onChange={(e) => setCancelState(s => ({ ...s, waiveCommission: e.target.checked }))} />}
                label="A jutalék elengedése (különben a foglalások a következő havi gyűjtőszámlára újra felkerülnek)"
              />
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setCancelState(null)}>Mégse</Button>
          <Button color="error" variant="contained" disabled={!cancelState?.reason.trim()} onClick={submitCancel}>
            Sztornózás{cancelState?.refund ? ' és visszatérítés' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!forceTarget} onClose={() => setForceTarget(null)}>
        <DialogTitle>Beragadt számla újrapróbálása</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A #{forceTarget?.id} számla kiállításakor a Billingo nem válaszolt egyértelműen, ezért elképzelhető,
            hogy a bizonylat már létrejött. Mielőtt újrapróbálod, ellenőrizd a Billingóban
            ({forceTarget?.provider?.businessName}, {formatHuf(forceTarget?.grossAmount)}), különben duplikált számla keletkezhet.
          </DialogContentText>
          {forceTarget?.lastError && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Utolsó hiba: {forceTarget.lastError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceTarget(null)}>Mégse</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => { const target = forceTarget; setForceTarget(null); retry(target, true); }}
          >
            Ellenőriztem, nem jött létre — újrapróbálás
          </Button>
        </DialogActions>
      </Dialog>
      {confirmDialog}
    </Box>
  );
};

export default AdminInvoicesPage;
